import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BulkAddGroupMembersDialog from './bulk-add-group-members-dialog';
import ChangeGroupMemberRoleDialog from './change-group-member-role-dialog';
import DeleteGroupMembersDialog from './delete-group-members-dialog';
import { Messages } from '../../../../utils/message-constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getUserPortalAllGroupMembers,
  groupSettingsQuery,
  getRoleOfGroupUserPortalQuery,
} from '../../../../api/users-api';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { DirectoryGroup } from '../../../../../types';
import { pagesConfig } from '../../../../shared/pages-config';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import GroupRemove from '@mui/icons-material/GroupRemove';
import Groups from '@mui/icons-material/Groups';
import Person from '@mui/icons-material/Person';
import Refresh from '@mui/icons-material/Refresh';
import Visibility from '@mui/icons-material/Visibility';

interface GroupMembershipUserPortalGridProps {
  group: DirectoryGroup;
}

export default function GroupMembershipUserPortalGrid({
  group,
}: GroupMembershipUserPortalGridProps) {
  const { groupEmail } = useParams() as { groupEmail: string };
  const { changeLogRoles, loggedInUser, userViewRoles, directoryRoles } =
    useUserSettings();
  const queryClient = useQueryClient();
  const [openAddMembers, setOpenAddMembers] = useState(false);
  const [openDeleteMembersFromGroup, setOpenDeleteMembersFromGroup] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openChangeRoles, setOpenChangeRoles] = useState(false);
  const navigate = useNavigate();

  const { data: groupSettings } = useQuery(
    groupSettingsQuery(groupEmail, true)
  );
  const { isLoading: isGroupRoleLoading, data: groupRole } = useQuery(
    getRoleOfGroupUserPortalQuery(true, loggedInUser, groupEmail)
  );

  const canSeeMembersUserPortal =
    (groupSettings?.whoCanViewMembership === 'ALL_MANAGERS_CAN_VIEW' &&
      groupRole == 'MANAGER') ||
    (groupSettings?.whoCanViewMembership === 'ALL_OWNERS_CAN_VIEW' &&
      groupRole == 'OWNER') ||
    (groupSettings?.whoCanViewMembership === 'ALL_MEMBERS_CAN_VIEW' &&
      (groupRole == 'MEMBER' ||
        groupRole == 'MANAGER' ||
        groupRole == 'OWNER')) ||
    groupSettings?.whoCanViewMembership === 'ALL_IN_DOMAIN_CAN_VIEW' ||
    false;

  const canModerateMembers =
    (groupSettings?.whoCanModerateMembers === 'OWNERS_AND_MANAGERS' &&
      groupRole == 'MANAGER') ||
    (groupSettings?.whoCanModerateMembers === 'OWNERS_AND_MANAGERS' &&
      groupRole == 'OWNER') ||
    (groupSettings?.whoCanModerateMembers === 'OWNERS_ONLY' &&
      groupRole == 'OWNER') ||
    groupSettings?.whoCanModerateMembers === 'ALL_MEMBERS' ||
    false;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.GroupMembers, groupEmail],
    queryFn: () => getUserPortalAllGroupMembers(groupEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group members'),
    },
    enabled: canSeeMembersUserPortal,
  });

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 2,
      display: 'flex',
      renderCell: (params) => {
        const emailDisplay =
          params.row.email === '*'
            ? 'All current and future users within domain'
            : params.row.email;
        const avatar = (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <Typography variant="body2">{emailDisplay}</Typography>
            {params.row.type === 'group' ? (
              <Tooltip arrow title="View Group">
                <span>
                  <IconButton
                    onClick={() =>
                      navigate(
                        `/${pagesConfig.groups.link}/${params.row.email}`
                      )
                    }
                  >
                    <Visibility />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        );
        return avatar;
      },
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      display: 'flex',
      valueGetter: (value: string) => {
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      },
      type: 'singleSelect',
      valueOptions: ['Owner', 'Manager', 'Member'],
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      display: 'flex',
      type: 'singleSelect',
      valueOptions: ['user', 'group'],
      renderCell: (params) => {
        const avatar = (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            {params.row.type === 'group' ? (
              <Tooltip arrow title="Group">
                <Groups />
              </Tooltip>
            ) : (
              <Tooltip arrow title="User">
                <Person />
              </Tooltip>
            )}
          </Stack>
        );
        return avatar;
      },
    },
  ];

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn>
        <Box>
          <Tooltip arrow title="Add Member">
            <span>
              <Button
                variant="contained"
                onClick={() => setOpenAddMembers(true)}
                disabled={!canModerateMembers}
              >
                Add Member
              </Button>
            </span>
          </Tooltip>

          <Tooltip arrow title="Edit Roles">
            <span>
              <IconButton
                onClick={() => setOpenChangeRoles(true)}
                disabled={
                  selectedRows.length === 0 ||
                  !canModerateMembers ||
                  selectedRows.includes('*')
                }
              >
                <Edit />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip arrow title="Delete">
            <span>
              <IconButton
                onClick={() => setOpenDeleteMembersFromGroup(true)}
                disabled={selectedRows.length === 0 || !canModerateMembers}
              >
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.GroupMembers, groupEmail],
                });
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.DirectoryGroup, groupEmail],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </SharedToolbar>
    );
  }

  if (isGroupRoleLoading) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (!canSeeMembersUserPortal) {
    return (
      <Alert severity="error">
        You do not have permission to view the members of this group. Please
        contact a manager or owner of the group to update yours and or the
        groups view members setting.
      </Alert>
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Members', <GroupRemove />),
        }}
        getRowId={(row) => row.email}
        rows={data || []}
        columns={columns}
        loading={isLoading || isFetching}
        disableColumnSelector
        checkboxSelection
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      {selectedRows.length > 0 && openDeleteMembersFromGroup ? (
        <DeleteGroupMembersDialog
          open={openDeleteMembersFromGroup}
          handleClose={() => setOpenDeleteMembersFromGroup(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          groupEmail={groupEmail}
          group={group}
          memberList={data || []}
          changeLogRoles={changeLogRoles}
          userView={true}
          isCloudIdentityEnabled={false}
          cloudIdentityGroupMembers={[]}
        />
      ) : null}
      {selectedRows.length > 0 && openChangeRoles ? (
        <ChangeGroupMemberRoleDialog
          open={openChangeRoles}
          handleClose={() => setOpenChangeRoles(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          groupEmail={groupEmail}
          group={group}
          memberList={data || []}
          changeLogRoles={changeLogRoles}
          userView={true}
        />
      ) : null}
      {openAddMembers ? (
        <BulkAddGroupMembersDialog
          userView={true}
          open={openAddMembers}
          handleClose={() => setOpenAddMembers(false)}
          groupEmail={groupEmail}
          memberList={data || []}
          changeLogRoles={changeLogRoles}
          directoryRoles={directoryRoles}
          userViewRoles={userViewRoles}
        />
      ) : null}
    </>
  );
}
